/**
 * The parts a property-submission review is made of, shared by the two places
 * a decision can be taken: the rail on Properties (inline) and the sheet opened
 * from the approvals table on Owners & access.
 *
 * They exist as one module so the two surfaces cannot drift into showing
 * different evidence for the same decision.
 */
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  useReviewPropertySubmission,
  type PropertySubmissionRow,
} from "@/hooks/v2/usePropertySubmissions";
import { useApartments, usePropertyRooms } from "@/hooks/v2/useProperties";

/**
 * Approve or reject one submission, with the note, the toast and the reset.
 *
 * `onDone` fires only on success — a failed decision leaves the reviewer where
 * they were, with what they typed still in the box.
 */
export function useSubmissionDecision(onDone: () => void) {
  const reviewMut = useReviewPropertySubmission();
  const { toast } = useToast();
  const [note, setNote] = useState("");

  function decide(
    submission: PropertySubmissionRow,
    decision: "approved" | "rejected"
  ) {
    reviewMut.mutate(
      { propertyId: submission.id, decision, note: note.trim() || null },
      {
        onSuccess: () => {
          toast({
            title:
              decision === "approved"
                ? "Property approved — still hidden"
                : "Submission rejected",
            /* Approval no longer publishes (see
               20260819090000_approval_does_not_publish.sql). The toast has to
               say so, or an approver walks away believing the building is on
               the public listing when it is not. */
            description:
              decision === "approved"
                ? `${submission.title} is approved. Publish it from the property page when its terms and photos are set.`
                : `${submission.title} was sent back to the owner.`,
          });
          setNote("");
          onDone();
        },
        onError: (err) =>
          toast({
            title: "Could not save decision",
            description: err instanceof Error ? err.message : "Unknown error",
            variant: "destructive",
          }),
      }
    );
  }

  return { note, setNote, decide, isPending: reviewMut.isPending };
}

const COLLECTION_MODE_LABEL: Record<string, string> = {
  company_collects: "coliviq collects rent",
  owner_collects: "Owner collects rent",
};

const REVENUE_MODEL_LABEL: Record<string, string> = {
  full_rent: "Rent arbitrage",
  pct_of_rent: "% of rent",
  flat_fee_per_room: "Flat fee per room",
};

/**
 * The units, rooms and proposed revenue terms exactly as the owner submitted
 * them. Reads the real apartment/room rows — staff RLS already allows it, so
 * no approval-specific endpoint is needed, and what the approver sees is what
 * will actually go live rather than a separate summary that can drift.
 */
export function SubmittedUnits({
  propertyId,
  /** Drops the size/terms lines that a 320px rail has no room for. */
  compact = false,
}: {
  propertyId: number;
  compact?: boolean;
}) {
  const { data: apartments, isLoading: aptLoading } = useApartments(propertyId);
  const { data: rooms, isLoading: roomLoading } = usePropertyRooms(propertyId);

  if (aptLoading || roomLoading) {
    return (
      <p className="text-xs text-muted-foreground">Loading submitted units…</p>
    );
  }
  const apts = apartments ?? [];
  const allRooms = rooms ?? [];
  if (apts.length === 0 && allRooms.length === 0) return null;

  return (
    <div>
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Submitted units &amp; rooms
      </h4>
      <div className="space-y-3">
        {apts.map((apt) => {
          const aptRooms = allRooms.filter(
            (r) =>
              (r as { apartment_id?: number | null }).apartment_id === apt.id
          );
          const revenueBits = [
            apt.collection_mode
              ? (COLLECTION_MODE_LABEL[apt.collection_mode] ??
                apt.collection_mode)
              : null,
            apt.revenue_model
              ? (REVENUE_MODEL_LABEL[apt.revenue_model] ?? apt.revenue_model)
              : null,
            apt.coliviq_fee_pct != null ? `${apt.coliviq_fee_pct}%` : null,
            apt.coliviq_fee_amount != null
              ? `€${apt.coliviq_fee_amount}/room`
              : null,
          ].filter(Boolean);

          return (
            <div key={apt.id} className="rounded-lg border">
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b bg-muted/40 px-3 py-2">
                <span className="text-sm font-semibold">
                  {apt.label}
                  <span className="ml-2 font-normal text-muted-foreground">
                    {[apt.floor, apt.size_sqm ? `${apt.size_sqm} m²` : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {aptRooms.length} {aptRooms.length === 1 ? "room" : "rooms"}
                </span>
              </div>
              {revenueBits.length > 0 && (
                <p className="border-b px-3 py-1.5 text-xs text-muted-foreground">
                  {compact ? "" : "Proposed terms: "}
                  {revenueBits.join(" · ")}
                </p>
              )}
              {aptRooms.length === 0 ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">
                  No rooms submitted for this unit.
                </p>
              ) : (
                <ul className="divide-y">
                  {aptRooms.map((room) => (
                    <li
                      key={room.id}
                      className="flex items-baseline justify-between gap-3 px-3 py-1.5 text-sm"
                    >
                      <span className="truncate">{room.title}</span>
                      <span className="shrink-0 text-right text-xs text-muted-foreground">
                        {[
                          room.price != null ? `€${room.price}` : null,
                          room.size_sqm ? `${room.size_sqm} m²` : null,
                          compact ? null : room.floor,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function KvRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b py-1.5 last:border-b-0">
      <dt className="shrink-0 text-xs text-muted-foreground">{k}</dt>
      <dd className="text-right font-medium">{v}</dd>
    </div>
  );
}
